import { Component, EventEmitter, Output, OnDestroy } from '@angular/core'
import { ClusteringNormalization, PipelineType, TsneParameters } from '../../../shared/model/jobs'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Subject, take, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import {
    selectCaseMarkers,
    selectLoadedSegObjectPropertyMaps,
} from '../../../state/case/case.selectors'
import { SegObjectPropertyMap } from '../../../state/case/case.state'
import { JobApiService } from 'src/app/shared/services/job-api.service'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { selectImageLayers } from 'src/app/state/viewer/viewer.selectors'

@Component({
    selector: 'app-auto-cell-detect',
    templateUrl: './auto-cell-detect.component.html',
    styleUrls: ['./auto-cell-detect.component.scss'],
})
export class AutoCellDetectComponent implements OnDestroy {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)

    autoCellDetectForm: FormGroup

    createJobMode = true
    selectedMarkers: string[] = []
    readonly clusteringMarkers$ = this.store.select(selectCaseMarkers)
    readonly NEW_EXPERIMENT_NAME_KEY = 'newExperimentName'
    readonly LAYER_TYPE_MARKERS_KEY = 'markers'
    readonly LAYER_TYPE_TRANSCRIPTS_KEY = 'transcripts'
    readonly LAYER_TYPE_MORPHOMETRICS_KEY = 'morphometrics'

    readonly layerTypes = [
        { name: 'Marker', formControl: this.LAYER_TYPE_MARKERS_KEY },
        { name: 'Transcripts', formControl: this.LAYER_TYPE_TRANSCRIPTS_KEY },
        { name: 'Morphometrics', formControl: this.LAYER_TYPE_MORPHOMETRICS_KEY },
    ]

    private readonly destroy$ = new Subject<void>()
    enableJobButton = false
    private loadedIntensityMaps: SegObjectPropertyMap[] = []
    readonly loadedSegObjectPropertyMaps$ = this.store.select(selectLoadedSegObjectPropertyMaps)
    readonly sourceLayers$ = this.store.select(selectImageLayers)

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService
    ) {
        this.loadedSegObjectPropertyMaps$
            .pipe(takeUntil(this.destroy$))
            .subscribe(maps => {
                this.loadedIntensityMaps = maps
                this.updateEnableJobButton()
            })

        this.initiateForm()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    @Output() paramsSet = new EventEmitter<TsneParameters>()

    startJob(): void {
        if (!this.enableJobButton) return

        const inputFeature = ['markers', 'morphometrics', 'transcripts'].filter(
            feature =>
                this.autoCellDetectForm.value[feature] ||
                (feature === 'markers' && this.selectedMarkers.length > 0)
        )
        const formData = {
            ...this.autoCellDetectForm.value,
            input_feature: inputFeature,
            markers: this.selectedMarkers,
            // Same map object shape as before; list when multiple are loaded (backend TBD).
            seg_object_property_maps: this.loadedIntensityMaps,
        }

        this.jobApi
            .startClusteringJob({
                type: PipelineType.AutoCellDetection,
                ...formData,
            })
            .subscribe({
                next: () => {
                    closeJobSelectionDialog()
                },
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Error starting job')
                },
            })
        this.resetForm()
    }

    private initiateForm(): void {
        this.autoCellDetectForm = this.formBuilder.group({
            [this.NEW_EXPERIMENT_NAME_KEY]: new FormControl('', [Validators.required]),
            [this.LAYER_TYPE_MARKERS_KEY]: new FormControl(false, [Validators.required]),
            [this.LAYER_TYPE_TRANSCRIPTS_KEY]: new FormControl(false, [Validators.required]),
            [this.LAYER_TYPE_MORPHOMETRICS_KEY]: new FormControl(false, [Validators.required]),
        })

        this.autoCellDetectForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.updateEnableJobButton()
        })
        this.updateEnableJobButton()
    }

    selectMarkers(markerName: string): void {
        if (this.selectedMarkers.includes(markerName)) {
            this.selectedMarkers = this.selectedMarkers.filter(marker => marker !== markerName)
        } else {
            this.selectedMarkers.push(markerName)
        }
        this.updateEnableJobButton()
    }

    onMarkerCheckboxChange(): void {
        this.sourceLayers$.pipe(take(1)).subscribe(markers => {
            const availableMarkers = markers.filter(m => m.name).map(m => m.name!)

            const allSelected =
                availableMarkers.length > 0 &&
                availableMarkers.every(marker => this.selectedMarkers.includes(marker))

            if (allSelected) {
                this.selectedMarkers = []
                this.autoCellDetectForm.get(this.LAYER_TYPE_MARKERS_KEY)?.setValue(false)
            } else {
                this.selectedMarkers = [...availableMarkers]
                this.autoCellDetectForm.get(this.LAYER_TYPE_MARKERS_KEY)?.setValue(true)
            }
            this.updateEnableJobButton()
        })
    }

    private updateEnableJobButton(): void {
        if (!this.autoCellDetectForm) return

        const formValue = this.autoCellDetectForm.value
        const hasIntensityMap = this.loadedIntensityMaps.length > 0
        const hasExperimentName = !!formValue[this.NEW_EXPERIMENT_NAME_KEY]

        this.enableJobButton =
            hasIntensityMap &&
            hasExperimentName &&
            (this.autoCellDetectForm.value[this.LAYER_TYPE_MORPHOMETRICS_KEY] ||
                this.autoCellDetectForm.value[this.LAYER_TYPE_TRANSCRIPTS_KEY] ||
                this.selectedMarkers.length > 1) &&
            this.selectedMarkers.length !== 1
    }

    private resetForm(): void {
        this.selectedMarkers = []
        this.autoCellDetectForm.reset({
            [this.NEW_EXPERIMENT_NAME_KEY]: '',
            [this.LAYER_TYPE_MARKERS_KEY]: false,
            [this.LAYER_TYPE_TRANSCRIPTS_KEY]: false,
            [this.LAYER_TYPE_MORPHOMETRICS_KEY]: false,
        })
        this.updateEnableJobButton()
    }
}
